import { Button } from '@prio365/prio365-react-library';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import { ResizeCallback } from 're-resizable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { createSelector } from 'reselect';
import {
  getActiveProject,
  getActiveProjectInMessageCenter,
  getAllProjects,
  getIsFetchingComments,
  getIsSettingEnabled,
  getLastOpenDriveItemFolder,
  getMessage,
  getWidgetAreaActiveProjectId,
  getWidgetAreaDisplayState,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import Flex from '../../../components/Flex';
import { Message } from '../../../models/Message';
import { Project } from '../../../models/Project';
import { DriveItemId, ProjectId } from '../../../models/Types';
import { makePrioStyles } from '../../../theme/utils';
import { updateDriveItemPath } from '../../documents/actions/lastOpenDriveItemFolder';
import DocumentsWidget from '../../documents/components/Widget/DocumentsWidget';
import UploadManager from '../../documents/components/Widget/UploadManager';
import WidgetBarUploadManagerNavigationButton from '../../documents/components/Widget/WidgetBarUploadManagerNavigationButton';
import { fetchConversationComments } from '../../mail/actions/projects/mailCommentActions';
import WidgetMailSelectionList from '../../mail/components/WidgetMailSelectionList';
import useProjectsContext from '../../projects/hooks/useProjectsContext';
import TasksWidget from '../../tasks/components/TasksWidget';
import {
  closeWidgetArea,
  openWidgetArea,
  selectWidget,
  setActiveDriveItemDocumentsWidget,
  setActiveProjectWidgetBar,
} from '../actions';
import { Widget } from '../reducers';
import AccountingWidget from './AccountingWidget';
import PrioShareWidget from './prioShareWidget/PrioShareWidget';
import MailComments from './MailComment';
import MailTaskList from './MailTaskList';
import WidgetAreaResizable from './WidgetAreaResizable';
import { IconName } from '@fortawesome/fontawesome-svg-core';

const panelDefaultWidth = 450;
const panelMaxWidth = document.body.clientWidth - 380 - 320;
const iconBarWidth = 48;

const useStyles = makePrioStyles((theme) => ({
  root: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    borderLeft: theme.old.borders.content,
    boxShadow: theme.old.palette.boxShadow.regular,
    zIndex: 500,
    backgroundColor: theme.old.palette.backgroundPalette.content,
  },
  hiddenElement: {
    height: '100%',
  },
  panelChild: {
    overflowX: 'hidden',
    overflowY: 'auto',
    height: '100%',
    borderLeft: theme.old.borders.content,
    position: 'relative',
    flexShrink: 0,
  },
  iconBar: {
    display: 'flex',
    flexDirection: 'column',
    width: iconBarWidth,
    flexShrink: 0,
    alignItems: 'center',
  },
  toggleButton: {
    background: 'transparent',
    marginBottom: theme.old.spacing.unit(1),
  },
  iconButtonActive: {
    backgroundColor: theme.colors.application.background.selected,
  },
  documents: {
    height: '100%',
    width: '100%',
    '& .ant-upload': {
      height: '100%',
      width: '100%',
    },
  },
  iconButton: {
    transition: 'font-size 0.25s ease',
  },
  yellowDot: {
    position: 'absolute',
    top: '5px',
    right: '4px',
    width: '8px',
    height: '8px',
    backgroundColor: '#E2B203',
    borderRadius: '999px',
    padding: '4px',
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: '#E0E6EE',
    margin: '10px 0',
  },
}));

const isMyProjectSelector = (projectId: ProjectId) =>
  createSelector<[(state: RootReducerState) => Project[]], boolean>(
    getAllProjects,
    (projects) =>
      !projectId ||
      projectId === 'me' ||
      (projects ?? []).map((project) => project.projectId).includes(projectId)
  );

interface WidgetAreaProps {
  disablePersonalMessages?: boolean;
  selectedProjectId?: ProjectId;
}

export const WidgetArea: React.FC<WidgetAreaProps> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { disablePersonalMessages, selectedProjectId } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getProjectById } = useProjectsContext();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { open, selectedWidget } = useSelector(getWidgetAreaDisplayState);
  const activeProjectId = useSelector(getActiveProject);
  const activeProject = getProjectById(activeProjectId);
  const messageCenterProjectId = useSelector(getActiveProjectInMessageCenter);

  const isBetaEnabled = useSelector<RootReducerState, boolean>((state) =>
    getIsSettingEnabled(state, 'General_BetaFeaturesEnabled')
  );
  const isFinanceEnabled = useSelector<RootReducerState, boolean>((state) =>
    getIsSettingEnabled(state, 'General_FinanceServicesEnabled')
  );

  const isAccountingWidgetEnabled = useMemo(() => {
    return isBetaEnabled && isFinanceEnabled;
  }, [isBetaEnabled, isFinanceEnabled]);

  const prioShareExtension = useMemo(() => {
    if (!activeProject) return null;
    return (
      activeProject.projectExtensionMeDtos?.find(
        (e) => e.projectExtensionType === 'prioShare'
      ) ?? null
    );
  }, [activeProject]);

  const isPrioShareReady =
    prioShareExtension?.extensionUserState === 'ready';
  const isPrioShareExists = !!prioShareExtension;

  const widgetAreaProjectId = useSelector(getWidgetAreaActiveProjectId);
  const [messageId, setMessageId] = useState<string>(null);
  const location = useLocation();

  const [size, setSize] = useState<number>(panelDefaultWidth);

  const width = useMemo(() => (open ? size : iconBarWidth), [open, size]);

  const isMyProject = useSelector(isMyProjectSelector(widgetAreaProjectId));

  const lastOpenFolderDriveItemIds = useSelector((state: RootReducerState) =>
    getLastOpenDriveItemFolder(state, widgetAreaProjectId)
  );

  const isMe: boolean = (selectedProjectId ?? activeProjectId) === 'me';
  const currentMessage = useSelector<RootReducerState, Message>((state) =>
    messageId ? getMessage(state, activeProjectId, messageId) : null
  );

  const [emailConversationId, setEmailConversationId] = useState<string>(null);

  const isFetching = useSelector<RootReducerState, boolean>((state) =>
    getIsFetchingComments(state)
  );

  const [newDriveItemDestinationId, setNewDriveItemDestinationId] =
    useState<DriveItemId>(null);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const toggle = () => {
    if (open) {
      dispatch(closeWidgetArea());
    } else {
      dispatch(openWidgetArea());
    }
  };

  const resetSize = () => {
    setSize(panelDefaultWidth);
  };

  const openAreaAndSelectWidget = (widget: Widget) => {
    if (!open) {
      dispatch(openWidgetArea(widget));
    } else if (widget !== selectedWidget) {
      dispatch(selectWidget(widget));
    } else if (open && widget === selectedWidget) {
      dispatch(closeWidgetArea());
    }
  };

  const handleResizeStop: ResizeCallback = (e, direction, ref, delta) => {
    if (!open) {
      if (delta.width + iconBarWidth < panelDefaultWidth) {
        setSize(panelDefaultWidth);
      } else {
        setSize(delta.width + iconBarWidth);
      }
      dispatch(openWidgetArea());
    } else {
      setSize(delta.width + size);
    }
  };

  const setProjectId = useCallback(
    (projectId: ProjectId) => {
      dispatch(setActiveProjectWidgetBar(projectId));
      setNewDriveItemDestinationId(null);
    },
    [dispatch]
  );

  //#endregion

  //#region ------------------------------ components
  const icon: (
    iconName: IconName,
    widget: Widget,
    disabled?: boolean,
    style?: React.CSSProperties,
    showYellowDot?: boolean
  ) => React.ReactNode = (
    iconName,
    widget,
    disabled = false,
    style,
    showYellowDot
  ) => (
    <Tooltip title={t(`common:widgetArea.${widget}`)} placement="left">
      <div style={{ position: 'relative' }}>
        <Button
          onClick={openAreaAndSelectWidget.bind(this, widget)}
          type="link"
          size="large"
          iconProp={
            iconName === 'message-bot' ? ['far', iconName] : ['fal', iconName]
          }
          className={classNames(classes.iconButton, {
            [classes.iconButtonActive]: widget === selectedWidget,
          })}
          disabled={disabled}
          style={{ marginTop: '8px', width: '44px', ...style }}
        />
        {showYellowDot && <span className={classes.yellowDot}></span>}
      </div>
    </Tooltip>
  );

  const mailNotiz: (messageId: string) => React.ReactNode = (messageId) => (
    <MailComments
      sharedMailboxId={activeProject?.sharedMailboxUserId}
      messageId={messageId}
      isFetching={isFetching}
      emailConversationId={emailConversationId}
      setEmailConversationId={setEmailConversationId}
      projectId={activeProjectId}
    />
  );

  const accounting: (projectId: ProjectId) => React.ReactNode = (projectId) => (
    <AccountingWidget projectId={projectId} />
  );

  const documentWidget = (
    <DocumentsWidget
      projectId={widgetAreaProjectId}
      className={classes.documents}
      size={size}
      initialDriveItemId={
        newDriveItemDestinationId ?? lastOpenFolderDriveItemIds?.documentsWidget
      }
      onProjectIdChange={setProjectId}
      setNewDriveItemDestinationId={setNewDriveItemDestinationId}
    />
  );

  //#endregion

  let widget: React.ReactNode = <></>;

  switch (selectedWidget) {
    case 'mail': {
      if (!!activeProjectId && !isMe) {
        widget = !disablePersonalMessages ? (
          <WidgetMailSelectionList projectId={activeProjectId} />
        ) : null;
      }
      break;
    }
    case 'planner': {
      widget = !!activeProject?.masterPlanId && (
        <TasksWidget projectId={activeProjectId} />
      );
      break;
    }
    case 'documents': {
      widget = documentWidget;
      break;
    }
    case 'uploadManager': {
      widget = (
        <UploadManager
          projectId={activeProjectId}
          onProjectIdChange={setProjectId}
          onParentDriveItemClick={(driveItemId) => {
            dispatch(
              updateDriveItemPath(activeProjectId, {
                documentsWidget: driveItemId,
              })
            );
          }}
        />
      );
      break;
    }
    case 'task': {
      widget = (
        <MailTaskList projectId={activeProjectId} messageId={messageId} />
      );
      break;
    }
    case 'comment': {
      if (activeProject && messageId) {
        widget = mailNotiz(messageId);
      } else {
        widget = documentWidget;
        dispatch(selectWidget('documents'));
      }
      break;
    }
    case 'accounting': {
      if (isAccountingWidgetEnabled) {
        widget = accounting(activeProjectId);
      } else {
        widget = documentWidget;
        dispatch(closeWidgetArea('documents'));
      }
      break;
    }
    case 'prioShare': {
      if (isPrioShareReady) {
        widget = (
          <PrioShareWidget
            projectId={activeProjectId}
            projectExtensionId={prioShareExtension?.projectExtensionId}
          />
        );
      } else {
        widget = documentWidget;
        dispatch(closeWidgetArea('documents'));
      }
      break;
    }
  }

  //#region ------------------------------ Effects
  useEffect(() => {
    if (selectedWidget === 'mail' && disablePersonalMessages) {
      dispatch(closeWidgetArea(null));
    }
  }, [selectedWidget, disablePersonalMessages, dispatch]);

  useEffect(() => {
    if (!isMyProject && selectedWidget !== 'documents') {
      dispatch(closeWidgetArea());
    }
  }, [isMyProject, selectedWidget, dispatch]);

  useEffect(() => {
    if (selectedProjectId) {
      setProjectId(selectedProjectId);
    } else if (activeProjectId !== 'me') {
      setProjectId(activeProjectId);
    } else if (messageCenterProjectId && messageCenterProjectId !== 'me') {
      setProjectId(messageCenterProjectId);
    }
  }, [
    selectedProjectId,
    activeProjectId,
    messageCenterProjectId,
    setProjectId,
  ]);

  useEffect(() => {
    if (open && selectedWidget === 'mail' && (isMe || !widgetAreaProjectId)) {
      dispatch(closeWidgetArea('documents'));
    }
  }, [open, isMe, widgetAreaProjectId, selectedWidget, dispatch]);

  useEffect(() => {
    if (emailConversationId) {
      dispatch(fetchConversationComments(emailConversationId));
    }
  }, [dispatch, emailConversationId]);

  useEffect(() => {
    setEmailConversationId(
      currentMessage?.customSingleValueExtendedProperties?.emailConversationId
    );
  }, [currentMessage]);

  useEffect(() => {
    if (selectedWidget === 'comment' && !messageId) {
      dispatch(closeWidgetArea(null));
    }
  }, [selectedWidget, messageId, dispatch]);

  useEffect(() => {
    const match = location.pathname.match(/\/message\/([^/]+)\/details/);
    const newMessageId = match ? match[1] : null;
    setMessageId(newMessageId);
  }, [location]);

  useEffect(() => {
    if (
      selectedWidget === 'task' &&
      open &&
      !location.pathname.includes('/mail/')
    ) {
      dispatch(closeWidgetArea());
    }
  }, [location, selectedWidget, open, dispatch]);

  useEffect(() => {
    dispatch(
      setActiveDriveItemDocumentsWidget(
        newDriveItemDestinationId ?? lastOpenFolderDriveItemIds?.documentsWidget
      )
    );
  }, [
    dispatch,
    newDriveItemDestinationId,
    lastOpenFolderDriveItemIds?.documentsWidget,
  ]);
  //#endregion

  return (
    <>
      <div className={classes.hiddenElement} style={{ width: width }} />
      <WidgetAreaResizable
        onResizeStop={handleResizeStop}
        size={{ width: size, height: '100%' }}
        minWidth={panelDefaultWidth}
        maxWidth={panelMaxWidth}
        closedMinWidth={iconBarWidth}
        open={open}
      />
      <div
        className={classes.root}
        style={{ width: open ? size : iconBarWidth }}
      >
        <div className={classes.iconBar}>
          {!disablePersonalMessages &&
            !isMe &&
            icon('inbox', 'mail', !isMyProject)}
          {icon('folder-tree', 'documents')}
          {isAccountingWidgetEnabled && icon('file-upload', 'accounting')}
          {isPrioShareExists &&
            (isPrioShareReady ? (
              icon('share-nodes', 'prioShare')
            ) : (
              <Tooltip
                title={t('common:widgetArea.prioShareCreating')}
                placement="left"
              >
                <div style={{ position: 'relative' }}>
                  <Button
                    type="link"
                    size="large"
                    iconProp={['fal', 'share-nodes']}
                    className={classes.iconButton}
                    disabled
                    style={{ marginTop: '8px', width: '44px' }}
                  />
                </div>
              </Tooltip>
            ))}
          <WidgetBarUploadManagerNavigationButton
            onClick={() => openAreaAndSelectWidget('uploadManager')}
            isActive={selectedWidget === 'uploadManager'}
            widgetBarIsOpen={open}
            className={
              selectedWidget === 'uploadManager' && classes.iconButtonActive
            }
          />
          {!!activeProject?.masterPlanId &&
            icon('check', 'planner', !isMyProject)}
          {activeProjectId && (
            <>
              <div className={classes.divider}></div>
              {!isMe && messageId && (
                <>
                  {icon(
                    'comments',
                    'comment',
                    false,
                    { marginTop: '0px' },
                    !!emailConversationId
                  )}
                  {icon(
                    'list-check',
                    'task',
                    false,
                    { marginTop: '0px' },
                    !!currentMessage?.customSingleValueExtendedProperties
                      .messageTaskRelationId
                  )}
                </>
              )}
            </>
          )}
          <Flex.Item flex={1} />
          {size > panelDefaultWidth && open && (
            <Button
              type="link"
              onClick={resetSize}
              iconProp={['fal', 'chevron-right']}
              className={classes.toggleButton}
            />
          )}
          <Button
            type="link"
            onClick={toggle}
            iconProp={[
              'fal',
              open ? 'chevron-double-right' : 'chevron-double-left',
            ]}
            className={classes.toggleButton}
          />
        </div>
        {open && (
          <div
            className={classes.panelChild}
            style={{ width: size - iconBarWidth - 1 }}
          >
            {isMyProject ? widget : null}
          </div>
        )}
      </div>
    </>
  );
};

export default WidgetArea;
